import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronLeft, MapPin, Calendar, CreditCard, QrCode, Wallet, Loader2 } from 'lucide-react'
import { btnPrimary, btnSecondary, SkeletonRows, useConfirm } from '@/components/rm'
import { useT } from '@/i18n'
import { fmtDateTime, fmtTime, fmtWeekday, pesoFromCentavos } from '@/lib/ph'
import { useBookingActions, useMyBookings } from '@/lib/bookings'
import { PAY_METHODS, refundQuote, startCheckout, useBookingMoney, type PayMethod } from '@/lib/escrow'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { BK_STATUS, BK_TONE } from './bookings'
import { useBookingDispute } from '@/lib/disputes'
import { DisputeBanner, ReportProblem } from '@/components/dispute'

// Spec 007. One booking, client side. Payment is spec 008; disputes are spec 009.
export const Route = createFileRoute('/_client/bookings_/$id')({ component: BookingDetail })

function BookingDetail() {
  const { id } = Route.useParams()
  const { t } = useT()
  const q = useMyBookings()
  const { cancel } = useBookingActions()
  const [confirm, confirmDialog] = useConfirm()
  const money = useBookingMoney()
  const [notice, setNotice] = useState('')
  const b = q.data?.find((x) => x.id === id)
  const dispute = useBookingDispute(id, !!b && ['disputed', 'released', 'refunded'].includes(b.status))
  if (q.isLoading) return <SkeletonRows count={4} />
  if (!b) return <p className="text-[14px] text-token-secondary">{t('tasks.notFound')}</p>
  const by = b.cancelled_by === 'client' ? t('bk.byClient') : b.cancelled_by === 'business' ? t('bk.byBusiness') : t('bk.bySystem')

  return (
    <div className="space-y-4">
      {confirmDialog}
      <Link to="/bookings" className="inline-flex items-center gap-1 text-[13px] font-semibold text-token-secondary"><ChevronLeft className="h-4 w-4" />{t('bk.nav')}</Link>
      <section className="space-y-3 rounded-[16px] border border-token-subtle bg-token-surface p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display text-[20px] font-bold leading-tight text-token-primary">{b.title}</h1>
            {b.business_slug ? <Link to="/p/$slug" params={{ slug: b.business_slug }} className="text-[13px] font-semibold text-brand hover:underline">{b.business_name}</Link>
              : <p className="text-[13px] text-token-secondary">{b.business_name}</p>}
          </div>
          <span className={cn('flex-none rounded-full px-2.5 py-0.5 text-[12px] font-semibold', BK_TONE[b.status])}>{t(BK_STATUS[b.status])}</span>
        </div>
        <div className="space-y-1 text-[13.5px] text-token-primary">
          <p className="flex items-center gap-2"><Calendar className="h-4 w-4 text-token-muted" />{t('bk.when', { date: fmtWeekday(b.scheduled_start), time: fmtTime(b.scheduled_start) })}</p>
          <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-token-muted" />{[b.address_line, b.barangay, b.city].filter(Boolean).join(', ')}</p>
        </div>
        {b.note && <p className="whitespace-pre-line text-[13.5px] text-token-secondary">{b.note}</p>}
        <div className="flex items-center justify-between rounded-[12px] bg-token-selected px-4 py-3">
          <span className="text-[13.5px] font-semibold text-token-secondary">{t('bk.price')}</span>
          <span className="font-display text-[20px] font-bold tabular-nums text-token-primary">{pesoFromCentavos(b.price_centavos)}</span>
        </div>
        {(b.status === 'cancelled' || b.status === 'expired') && (
          <p className="text-[13px] text-token-muted">{t('bk.cancelledBy', { who: by })}{b.cancel_reason ? `: ${b.cancel_reason}` : ''}</p>
        )}
      </section>
      {dispute.data && <DisputeBanner dispute={dispute.data} business={b.business_name} />}
      {b.status === 'awaiting_payment' && <PaySheet bookingId={b.id} business={b.business_name} priceCentavos={b.price_centavos} />}
      {b.status === 'paid' && <p className="rounded-[12px] bg-token-success-surface px-4 py-3 text-[13.5px] font-semibold text-token-success">{t('pay.paidHeld')}</p>}
      {b.status === 'done' && (
        <section className="space-y-3 rounded-[16px] border border-token-warning bg-token-warning-surface p-4">
          <p className="text-[15px] font-bold text-token-primary">{t('pay.doneTitle', { business: b.business_name })}</p>
          <p className="text-[13.5px] text-token-secondary">{t('pay.doneBody', { date: b.auto_release_at ? fmtDateTime(b.auto_release_at) : '' })}</p>
          <button type="button" className={`${btnPrimary} h-11 w-full justify-center`} disabled={money.confirmDone.isPending} onClick={async () => {
            const ok = await confirm({ title: t('pay.confirm'), body: t('pay.confirmBody', { business: b.business_name }), confirmLabel: t('pay.confirm'), cancelLabel: t('tasks.keep') })
            if (ok) await money.confirmDone.mutateAsync(b.id)
          }}>{t('pay.confirm')}</button>
          <ReportProblem bookingId={b.id} className="h-10 w-full" />
        </section>
      )}
      {b.status === 'released' && !dispute.data?.decision && <p className="rounded-[12px] bg-token-success-surface px-4 py-3 text-[13.5px] font-semibold text-token-success">{t('pay.released', { business: b.business_name })}</p>}
      {b.status === 'refunded' && <p className="rounded-[12px] bg-token-hover px-4 py-3 text-[13.5px] font-semibold text-token-secondary">{t('pay.refunded', { price: pesoFromCentavos(b.refund_centavos ?? 0) })}</p>}
      {(b.status === 'requested' || b.status === 'awaiting_payment') && (
        <button type="button" className={`${btnSecondary} h-10 w-full justify-center`} onClick={async () => {
          const ok = await confirm({ title: t('bk.cancel'), body: t('bk.cancelConfirm'), confirmLabel: t('bk.cancel'), cancelLabel: t('tasks.keep'), tone: 'danger' })
          if (ok) await cancel.mutateAsync({ booking: b.id })
        }}>{t('bk.cancel')}</button>
      )}
      {b.status === 'paid' && (
        <>
          <button type="button" className={`${btnSecondary} h-10 w-full justify-center`} disabled={money.cancelPaid.isPending} onClick={async () => {
            const q = await refundQuote(b.id)
            if (!q.allowed) { setNotice(t('pay.refundTooLate')); return }
            const ok = await confirm({ title: t('pay.cancelPaid'), body: t('pay.refundQuote', { price: pesoFromCentavos(q.amount ?? 0) }), confirmLabel: t('pay.cancelPaid'), cancelLabel: t('tasks.keep'), tone: 'danger' })
            if (!ok) return
            try { await money.cancelPaid.mutateAsync({ booking: b.id }) } catch { setNotice(t('pay.refundFailed')) }
          }}>{t('pay.cancelPaid')}</button>
          <ReportProblem bookingId={b.id} className="h-10 w-full" />
          {notice && <p role="alert" className="text-center text-[13px] text-token-warning">{notice}</p>}
        </>
      )}
    </div>
  )
}

const METHOD_ICON: Record<PayMethod, typeof QrCode> = { qrph: QrCode, gcash: Wallet, paymaya: Wallet, card: CreditCard }
const METHOD_KEY = { qrph: 'pay.qrph', gcash: 'pay.gcash', paymaya: 'pay.paymaya', card: 'pay.card' } as const

/** Spec 008 US1: QR Ph preselected; the partner's checkout opens for the chosen method. */
function PaySheet({ bookingId, business, priceCentavos }: { bookingId: string; business: string; priceCentavos: number }) {
  const { t } = useT()
  const [method, setMethod] = useState<PayMethod>('qrph')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const pay = async () => {
    setBusy(true); setError('')
    try { window.location.assign(await startCheckout(bookingId, method)) } catch (e) { setError(t((e as Error).message === 'not_verified' ? 'pay.notVerified' : 'pay.failed')); setBusy(false) }
  }
  return (
    <section className="space-y-3 rounded-[16px] border border-token-subtle bg-token-surface p-4">
      <p className="text-[15px] font-bold text-token-primary">{t('pay.title')}</p>
      <div role="radiogroup" className="space-y-2">
        {PAY_METHODS.map((m) => {
          const Icon = METHOD_ICON[m]
          return (
            <button key={m} type="button" role="radio" aria-checked={method === m} onClick={() => setMethod(m)}
              className={cn('flex w-full items-start gap-3 rounded-[12px] border px-3 py-2.5 text-left', method === m ? 'border-brand bg-token-selected' : 'border-token-subtle')}>
              <Icon className={cn('mt-0.5 h-5 w-5 flex-none', method === m ? 'text-brand' : 'text-token-muted')} />
              <span className="min-w-0">
                <span className="block text-[14.5px] font-semibold text-token-primary">{t(METHOD_KEY[m])}</span>
                {m === 'qrph' && <span className="block text-[12px] text-token-secondary">{t('pay.qrphNote')}</span>}
              </span>
            </button>
          )
        })}
      </div>
      <p className="text-[12.5px] text-token-secondary">{t('pay.held', { business })}</p>
      {error && <p role="alert" className="text-[13px] text-token-danger">{error}</p>}
      <button type="button" onClick={pay} disabled={busy} className={`${btnPrimary} h-11 w-full justify-center`}>
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" />{t('pay.opening')}</> : t('pay.continue', { price: pesoFromCentavos(priceCentavos) })}
      </button>
    </section>
  )
}
